// Pilot (pharmacie pilote) display rules, kept pure so they are unit-tested (lib/pilots.test.js) instead of living as
// inline conditions in the components. Everything here mirrors what the BACKEND decides; nothing is computed or
// simulated on the client: health, capabilities and the sharing state all come from the API (backend/pilots/*).
import { inputToIso, isoToInput } from "./time";


// Prospect statuses from which a pharmacy can become a pilot (backend/pilots/models.py ELIGIBLE_PROSPECT_STATUSES).
export const PILOT_ELIGIBLE_PROSPECT_STATUSES = ["won", "onboarding_in_progress", "active_client"];

export const PILOT_STATUS_LABELS = { planned: "Planifié", active: "Actif", completed: "Terminé", cancelled: "Annulé" };
const PILOT_STATUS_CLASSES = {
  planned: "bg-slate-100 text-slate-700",
  active: "bg-teal-50 text-teal-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
};
export const pilotStatusClass = (status) => PILOT_STATUS_CLASSES[status] || PILOT_STATUS_CLASSES.planned;
export const isRunning = (pilot) => !!pilot && (pilot.status === "planned" || pilot.status === "active");
export const isClosed = (pilot) => !!pilot && (pilot.status === "completed" || pilot.status === "cancelled");

// "unknown" is deliberately NOT worded as a good state: absence of an alert is not good health.
export const HEALTH_LABELS = { on_track: "Sur la bonne voie", attention: "À surveiller", blocked: "Bloqué", unknown: "À qualifier" };
const HEALTH_TONES = {
  on_track: { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
  attention: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800" },
  blocked: { dot: "bg-red-500", chip: "bg-red-50 text-red-700" },
  unknown: { dot: "bg-slate-400", chip: "bg-slate-100 text-slate-600" },
};
export const healthTone = (state) => HEALTH_TONES[state] || HEALTH_TONES.unknown;
export const healthLabel = (state) => HEALTH_LABELS[state] || HEALTH_LABELS.unknown;

export const REASON_SEVERITY_LABELS = { blocked: "Blocage", attention: "Alerte", unknown: "Incertitude" };

export const AUTONOMY_LABELS = { not_evaluated: "Non évaluée", dependent: "Dépendante", partial: "Partielle", autonomous: "Autonome" };
// `not_evaluated` is the initial state: an evaluation always picks one of these three (backend ASSIGNABLE_AUTONOMY_LEVELS).
export const AUTONOMY_CHOICES = [
  { id: "dependent", label: "Dépendante", hint: "Besoin d'aide pour la plupart des tâches" },
  { id: "partial", label: "Partielle", hint: "Autonome sur certaines tâches" },
  { id: "autonomous", label: "Autonome", hint: "Utilise Jamora sans assistance" },
];

// OnePharma follow-up is DECLARATIVE: filled in by the team, never read from OnePharma.
export const SYNC_LABELS = { none: "Aucune synchronisation", active: "Synchronisation active", cut: "Synchronisation coupée" };
export const SYNC_CHOICES = [
  { id: "none", label: SYNC_LABELS.none }, { id: "active", label: SYNC_LABELS.active }, { id: "cut", label: SYNC_LABELS.cut },
];

export const ACTIVITY_SOURCE_LABELS = {
  interaction: "contact", task_completed: "tâche terminée", onboarding: "avancement de l'onboarding",
  migration: "avancement de la mise en place", autonomy: "évaluation d'autonomie", sync: "synchronisation déclarée",
  report: "compte rendu",
};

// The numbers that can be shared with the pilot's responsible (backend SHARE_KEYS), in display order.
export const SHARE_ITEMS = [
  { key: "whatsapp", label: "WhatsApp professionnel" },
  { key: "phone2", label: "Téléphone secondaire" },
  { key: "reference_phone", label: "Téléphone du référent" },
];

// Whether the "Pilote" tab exists at all: a pilot already exists, or the caller may create one for an eligible pharmacy.
export function shouldShowPilotTab({ pilotState, canManagePilot, prospectStatus }) {
  if (!pilotState || pilotState.status === "loading") return false;
  if (pilotState.status === "ready" || pilotState.status === "error") return true;
  if (pilotState.status !== "none") return false;  // not found / forbidden: nothing to show, nothing to create
  return !!canManagePilot && PILOT_ELIGIBLE_PROSPECT_STATUSES.includes(prospectStatus);
}

// What the API says the caller may do. Anything absent is "no": the UI never grants more than the backend.
export function capabilitiesOf(pilot) {
  const c = (pilot && pilot.capabilities) || {};
  return {
    managePlanning: c.manage_planning === true, manageLifecycle: c.manage_lifecycle === true,
    manageOperational: c.manage_operational === true, manageSharing: c.manage_sharing === true,
  };
}

export function validatePlanning({ start, end }) {
  if (start && end && end < start) return "La date de fin doit être postérieure ou égale à la date de début.";
  return null;
}

// Activation needs both dates (backend rule); returns what is still missing, in business words.
export function activationMissing({ start, end }) {
  const missing = [];
  if (!start) missing.push("la date de début");
  if (!end) missing.push("la date de fin");
  return missing;
}

// One row per shareable number: the value comes from the pharmacy's fiche (the caller is `full`, so they see it),
// `shared` = shared AND still valid, `stale` = was shared but the fiche's number changed since (masked by the backend).
export function buildShareRows(prospect, pilot) {
  const summary = (pilot && pilot.shared_contact) || {};
  const keys = new Set(summary.keys || []);
  const stale = new Set(summary.stale_keys || []);
  return SHARE_ITEMS.map(({ key, label }) => {
    const value = (prospect && prospect[key]) || "";
    return { key, label, value, hasValue: !!String(value).trim(), shared: keys.has(key), stale: stale.has(key) };
  });
}

export const sameSelection = (a, b) => a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

// Open pilot follow-up tasks of the pharmacy (domain pilots + type suivi_pilote), soonest due date first.
export function openFollowups(tasks) {
  return (tasks || [])
    .filter((t) => t.domain === "pilots" && t.task_type === "suivi_pilote" && !t.done)
    .sort((a, b) => (a.due_date || "9999-12-31").localeCompare(b.due_date || "9999-12-31"));
}

export function followupDefaults(pharmacyName) {
  return { title: `Suivi pilote : ${pharmacyName}`.trim(), due_date: "", priority: "medium" };
}

// The proposal to plan a follow-up is shown only when the backend itself raised "no follow-up" (never automatic).
export function proposesFollowup(pilot) {
  const reasons = (pilot && pilot.computed && pilot.computed.health && pilot.computed.health.reasons) || [];
  return isRunning(pilot) && reasons.some((r) => r.code === "no_followup");
}

export function activityText(activity) {
  if (!activity) return null;
  return activity.label || ACTIVITY_SOURCE_LABELS[activity.source] || activity.source;
}

// A short French sentence for an API failure, never a stack or a raw status.
export function pilotErrorMessage(error, fallback) {
  const status = error && error.response && error.response.status;
  const detail = error && error.response && error.response.data && error.response.data.detail;
  if (status === 403) return "Vous n'avez pas le droit d'effectuer cette action.";
  if (status === 404) return "Ce pilote n'existe plus ou n'est plus accessible.";
  if (typeof detail === "string" && detail) return detail;
  return fallback;
}

// ------------------------------------------------------------------ pilots list + operational workspace (P2-5)
export const pilotPath = (prospectId) => `/pilotes/${encodeURIComponent(prospectId)}`;

// "running" (planned + active) is the working default; "all" sends no status filter at all.
export const LIST_STATUS_TABS = [
  { id: "running", label: "En cours" },
  { id: "completed", label: "Terminés" },
  { id: "cancelled", label: "Annulés" },
  { id: "all", label: "Tous" },
];
export const LIST_HEALTH_CHOICES = ["blocked", "attention", "unknown", "on_track"];

// Server-side filters only: an empty/"all" value is simply not sent. The order stays the server's (newest first).
export function buildListParams({ status = "running", health = "", search = "", responsible = "", member = "", cursor = null, limit = 30 } = {}) {
  const params = { limit };
  if (status && status !== "all") params.status = status;
  if (health) params.health = health;
  if (responsible) params.responsible = responsible;
  if (member) params.member = member;  // "pilots whose team I am in" (principal or intervenant)
  const term = String(search || "").trim();
  if (term) params.search = term;
  if (cursor) params.cursor = cursor;
  return params;
}

// Appends a page to what is displayed; an id already shown is kept once (a refresh can overlap the previous page).
export function mergePages(shown, next) {
  const seen = new Set(shown.map((p) => p.id));
  return [...shown, ...next.filter((p) => !seen.has(p.id))];
}

// What a list row displays, from the API item only. A `limited` item has no prospect status/assignee and no assignees on the follow-up.
export function listRow(item) {
  const prospect = item.prospect || {};
  const computed = item.computed || {};
  const follow = computed.next_followup || null;
  return {
    id: item.id,
    prospectId: item.prospect_id,
    name: prospect.pharmacy_name || "Pharmacie",
    place: [prospect.commune, prospect.region].filter(Boolean).join(" · "),
    status: item.status,
    healthState: computed.health ? computed.health.state : null,
    responsible: item.responsible_name || null,
    nextFollowup: follow ? { title: follow.title, due: follow.due_date } : null,
    activity: activityText(computed.last_operational_activity),
    autonomy: item.autonomy ? item.autonomy.level : "not_evaluated",
    running: isRunning(item),
    limited: item.access === "limited",
    teamSize: item.team_size || 1,
    team: item.team_preview || [],
    role: item.role_in_pilot || null,
  };
}

// How a failed load of one pilot is explained: 404 and 403 both end on the same neutral screen (no existence oracle).
export function loadFailureKind(error) {
  const status = error && error.response && error.response.status;
  if (status === 404) return "notfound";
  if (status === 403) return "forbidden";
  return "error";
}


// A link to a prospect from inside the workspace: a `limited` caller has no prospect page, so it stays on the pilot.
export function workspaceLink(to, access, prospectId) {
  if (access === "limited" && /^\/prospects\//.test(to)) return pilotPath(prospectId);
  return to;
}

// The notice of a team member: they read the pilots-domain tasks of the whole team and edit only their own.
export const LIMITED_TASKS_NOTICE = "Vous voyez les tâches « Pharmacies pilotes » de toute l'équipe de ce pilote. Vous ne modifiez que les vôtres.";

// ------------------------------------------------------------------ team, team tasks and reports (multi-intervenants)
export const MAX_TEAM_SIZE = 20;  // principal included (backend MAX_TEAM_SIZE)
const ROLE_IN_PILOT_LABELS = { principal: "Principal", member: "Intervenant" };
export const roleInPilotLabel = (role) => ROLE_IN_PILOT_LABELS[role] || null;

// The team as the API gives it (principal first). Never trusts anything but explicit booleans from the backend.
export const teamOf = (pilot) => (pilot && Array.isArray(pilot.members) ? pilot.members : []);
export const canManageMembers = (pilot) => !!(pilot && pilot.capabilities && pilot.capabilities.manage_members === true);
export const isInTeam = (pilot, userId) => !!userId && teamOf(pilot).some((m) => m.id === userId);
// Intervenants WITHOUT a CRM account (collaborator fiches): shown and creditable, never given access (the backend keeps them apart from `members`).
export const intervenantsOf = (pilot) => (pilot && Array.isArray(pilot.intervenants) ? pilot.intervenants : []);

// « Réalisé par »: the people of the team who can be credited with work (active accounts and active intervenants). The backend re-checks it.
export function performerOptions(pilot) {
  return [
    ...teamOf(pilot).filter((m) => m.is_active !== false).map((m) => ({ kind: "user", id: m.id, name: m.name || "Compte supprimé", key: `user:${m.id}` })),
    ...intervenantsOf(pilot).filter((i) => !i.inactive).map((i) => ({ kind: "collaborator", id: i.id, name: i.name || "Intervenant", detail: i.job_title || "Sans compte CRM", key: `collaborator:${i.id}` })),
  ];
}
// « Réalisée par » when closing a task: offered only for a task of the pilots domain attached to THIS pilot's pharmacy (the server refuses it
// anywhere else).
export function taskPerformerOptions(task, pilot, prospectId) {
  if (!task || !pilot || task.domain !== "pilots" || !prospectId || task.prospect_id !== prospectId) return [];
  return performerOptions(pilot);
}
const performerKeys = (list) => (list || []).map((p) => `${p.kind}:${p.id}`).join(",");

// People a task of this pilot may be handed to by a team member: the ACTIVE people of the team (the backend refuses anyone else).
export function assignableMembers(pilot) {
  return teamOf(pilot).filter((m) => m.is_active !== false);
}

// Who the follow-up dialog may offer as assignee: a `tasks:scope_all` holder keeps the whole directory (existing behaviour); a team member picks
// among the team; anybody else can only keep the task for themselves (the backend forces it).
export function assigneeOptions({ pilot, directory, canScopeAll }) {
  if (canScopeAll) return (directory || []).map((u) => ({ id: u.id, name: u.name }));
  if (pilot && pilot.role_in_pilot) return assignableMembers(pilot).map((m) => ({ id: m.id, name: m.name }));
  return [];
}

export const TEAM_TASK_FILTERS = [{ id: "mine", label: "Mes tâches" }, { id: "team", label: "Équipe" }];

// Splits the team's tasks for display: open before done, then by due date; "mine" keeps only the caller's. Returns the groups by owner for the "team" view.
export function organizeTeamTasks(items, filter = "team") {
  const tasks = (items || []).filter((t) => filter !== "mine" || t.is_mine);
  const order = (a, b) => (Number(!!a.done) - Number(!!b.done)) || String(a.due_date || "9999-12-31").localeCompare(String(b.due_date || "9999-12-31"));
  const sorted = [...tasks].sort(order);
  if (filter === "mine") return { tasks: sorted, groups: [{ key: "mine", label: "Mes tâches", tasks: sorted }], counts: countTeamTasks(items) };
  const groups = new Map();
  for (const t of sorted) {
    const owners = (t.assignees || []).length ? t.assignees : [{ id: "none", name: "Sans responsable" }];
    const key = owners.map((o) => o.id).join("+");
    if (!groups.has(key)) groups.set(key, { key, label: owners.map((o) => o.name || "Compte supprimé").join(", "), mine: t.is_mine, tasks: [] });
    groups.get(key).tasks.push(t);
  }
  const list = [...groups.values()].sort((a, b) => (Number(!!b.mine) - Number(!!a.mine)) || a.label.localeCompare(b.label, "fr"));
  return { tasks: sorted, groups: list, counts: countTeamTasks(items) };
}

export function countTeamTasks(items) {
  const open = (items || []).filter((t) => !t.done);
  return { all: (items || []).length, open: open.length, mine: open.filter((t) => t.is_mine).length };
}

// What removing a member means for their open tasks: the manager is OFFERED a hand-over, never one imposed.
export function removalPlan({ pilot, memberId, teamTasks }) {
  const open = (teamTasks || []).filter((t) => !t.done && (t.assigned_to || []).includes(memberId));
  const candidates = assignableMembers(pilot).filter((m) => m.id !== memberId);
  return { openTasks: open.length, candidates, needsChoice: open.length > 0 };
}

// What can still be added to the team: active directory users not already in it, as long as the 20-person ceiling allows.
export function addableUsers({ pilot, directory }) {
  const inTeam = new Set(teamOf(pilot).map((m) => m.id));
  const room = MAX_TEAM_SIZE - inTeam.size - intervenantsOf(pilot).length;
  return { users: (directory || []).filter((u) => !inTeam.has(u.id)), room: Math.max(0, room) };
}

export const REPORT_KINDS = [
  { id: "visit", label: "Visite" }, { id: "call", label: "Appel" }, { id: "meeting", label: "Réunion" },
  { id: "training", label: "Formation" }, { id: "remote", label: "Assistance à distance" }, { id: "other", label: "Autre" },
];
export const REPORT_KIND_LABELS = Object.fromEntries(REPORT_KINDS.map((k) => [k.id, k.label]));
export const MAX_REPORT_CONTENT = 5000;
export const MAX_REPORT_TITLE = 120;

export function validateReport({ kind, title, content }) {
  if (!REPORT_KIND_LABELS[kind]) return "Choisissez le type de compte rendu.";
  if (!String(content || "").trim()) return "Le contenu du compte rendu est obligatoire.";
  if (String(content).trim().length > MAX_REPORT_CONTENT) return `Le contenu est limité à ${MAX_REPORT_CONTENT} caractères.`;
  if (String(title || "").trim().length > MAX_REPORT_TITLE) return `Le titre est limité à ${MAX_REPORT_TITLE} caractères.`;
  return null;
}

// "datetime-local" value <-> ISO instant. The form works in the person's own time zone (Profil), the API stores UTC.
export const toLocalInput = (iso) => isoToInput(iso);
export const fromLocalInput = (value) => inputToIso(value);

// The body of POST / PATCH …/reports: on an edit only what changed is sent (the backend keeps the previous state as a revision).
export function buildReportPayload(form, original = null) {
  const next = { kind: form.kind, title: String(form.title || "").trim() || null, content: String(form.content || "").trim(), occurred_at: fromLocalInput(form.occurred_at) };
  if (!original) {
    const body = { kind: next.kind, content: next.content };
    if (next.title) body.title = next.title;
    if (next.occurred_at) body.occurred_at = next.occurred_at;
    if ((form.performed_by || []).length) body.performed_by = form.performed_by.map(({ kind, id }) => ({ kind, id }));
    return body;
  }
  const body = {};
  if (next.kind !== original.kind) body.kind = next.kind;
  if (next.title !== (original.title || null)) body.title = next.title;
  if (next.content !== original.content) body.content = next.content;
  if (next.occurred_at && new Date(next.occurred_at).getTime() !== new Date(original.occurred_at).getTime()) body.occurred_at = next.occurred_at;
  if (form.performed_by && performerKeys(form.performed_by) !== performerKeys(original.performed_by)) body.performed_by = form.performed_by.map(({ kind, id }) => ({ kind, id }));
  return body;
}

// Prospects list: "pilot" filter and badge text, from the `pilot_status` GET /prospects adds (absent = not a pilot).
export const PROSPECT_PILOT_FILTERS = [
  { id: "all", label: "Pilote ou non" },
  { id: "pilots", label: "Pharmacies pilotes" },
  { id: "running", label: "Pilotes en cours" },
  { id: "none", label: "Non pilotes" },
];
export function matchesPilotFilter(prospect, filter) {
  const status = prospect.pilot_status;
  if (filter === "pilots") return !!status;
  if (filter === "running") return status === "planned" || status === "active";
  if (filter === "none") return !status;
  return true;
}
export const pilotTagLabel = (status) => (status === "completed" ? "Pilote terminé" : status === "cancelled" ? "Pilote annulé" : "Pilote");
